import CarrefourIcon from '#assets/svg/carrefour.svg?component';
import HomeIcon from '#assets/svg/home.svg?component';
import type { DataTableRowType } from '#components/elements/dataTable/dataTable.model';
import { translateDimensionCellText } from '#components/elements/dataTable/dataTable.utils';
import { formatIndicatorTitle, formatIndicatorValue, isValidNumber } from '#helpers';
import type {
  AvailableLanguagesType,
  DataTableCellType,
  DataTableColumn,
  DataTableViewType
} from '@carrefour-gcs/shared';
import { markRaw } from 'vue';

interface FormatDataTableRowParams {
  /** One raw line as returned by the API (cells keyed by column name) */
  row: Record<string, DataTableCellType | undefined>;
  /** Resolved columns definition (must be non-null) */
  columnsDefinition: Record<string, DataTableColumn>;
  /** Translate function (vue-i18n) */
  t: (key: string) => string;
  /** Current language code */
  language: AvailableLanguagesType;
  dataTableView: DataTableViewType;
  benchmarkIcon?: 'carrefour' | 'market';
}

/** Uses the icon for the explicit benchmark mode when given (home for Marché N+1, Carrefour otherwise); falls back to the per-view rule. */
function resolveBenchmarkIcon(benchmarkIcon: 'carrefour' | 'market' | undefined, dataTableView: DataTableViewType) {
  if (benchmarkIcon) {
    return benchmarkIcon === 'market' ? HomeIcon : CarrefourIcon;
  }
  return dataTableView === 'competitorVision' ? HomeIcon : CarrefourIcon;
}

export function formatDataTableRow({
  row,
  columnsDefinition,
  t,
  language,
  dataTableView,
  benchmarkIcon
}: FormatDataTableRowParams): DataTableRowType<string> {
  const columns = Object.keys(columnsDefinition);
  const formattedRow: DataTableRowType<string> = {};

  for (const column of columns) {
    const cell = row[column];

    if (!cell) {
      formattedRow[column] = { type: columnsDefinition[column]?.type ?? 'dimension', data: null };
    } else if ('text' in cell) {
      formattedRow[column] = {
        type: 'dimension',
        data: translateDimensionCellText(t, column, cell.text)
      };
    } else {
      if (cell.unit === 'text') {
        formattedRow[column] = {
          type: 'kpi',
          data: formatIndicatorTitle(cell)
        };
      } else {
        // For numerical values
        formattedRow[column] = {
          type: 'kpi',
          data: formatIndicatorValue({
            indicator: cell,
            withPlusSign: cell.withPlusSign,
            language
          }),
          rawData: formatIndicatorTitle(cell)
        };
      }

      if (cell.performance) {
        formattedRow[column].variationScore = formatIndicatorValue({
          indicator: cell.performance,
          withPlusSign: true,
          language
        });
        formattedRow[column].rawVariationScore = formatIndicatorTitle(cell.performance);
        if (isValidNumber(cell.performance?.value)) {
          formattedRow[column].variationScoreType = cell.performance.value < 0 ? 'negative' : 'positive';
        }
      }

      if (cell.benchmark) {
        formattedRow[column].variationScoreBenchmark = formatIndicatorValue({
          indicator: cell.benchmark,
          withPlusSign: true,
          language
        });
        formattedRow[column].rawVariationScoreBenchmark = formatIndicatorTitle(cell.benchmark);
        formattedRow[column].variationScoreBenchmarkIcon = markRaw(resolveBenchmarkIcon(benchmarkIcon, dataTableView));
      }
    }
  }

  return formattedRow;
}
